// AssetNode.tsx — the React Flow custom node for the graph explorer. Because
// React Flow nodes are real DOM, this is an ordinary shadcn/Tailwind card:
// type tile, name, up to ~3 property rows, an optional picture thumbnail, and a
// type-hashed accent border. Wrapped in React.memo (spec performance guardrail)
// so pans/zooms don't re-render every card.

import { memo } from 'react'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { IconTile } from '../ui/icon-tile'
import { AssetRelationsBadge } from '../asset-relations/AssetRelationsBadge'
import {
    mdiPlusCircleOutline,
    mdiMinusCircleOutline,
    mdiOpenInNew,
    mdiChevronUp,
    mdiAccountGroupOutline
} from '@mdi/js'

import { assetTypeColorVar, kindColorVar } from './colors'
import type { GraphNode, EntityKind } from './adapter'
import { SECTION_ICONS } from '../../lib/section-icons'

// The non-asset kinds an asset node can pull in on demand, with their toggle
// icon. Rendered as a compact toolbar row on every (non-pseudo) asset card.
const KIND_TOGGLES: Array<{ kind: EntityKind; icon: string }> = [
    { kind: 'document', icon: SECTION_ICONS.documents },
    { kind: 'intervention', icon: SECTION_ICONS.interventions },
    { kind: 'expense', icon: SECTION_ICONS.expenses },
    { kind: 'contract', icon: SECTION_ICONS.contracts },
    { kind: 'contact', icon: mdiAccountGroupOutline },
]

// Data carried on every React Flow node of type "asset".
export interface AssetNodeData extends Record<string, unknown> {
    graph: GraphNode
    isFocal: boolean
    isExpanded: boolean
    loading: boolean
    apiBaseUrl: string
    // Parent (upward) expansion: the immediate parent's id, if known, and
    // whether it is already expanded. Drives the top-edge chevron.
    parentUid?: string
    parentExpanded?: boolean
    // Which non-asset kinds are currently expanded for this asset.
    expandedKinds?: Set<EntityKind>
    onExpand: (id: string) => void
    onCollapse: (id: string) => void
    onOpen: (assetUid: string) => void
    onLoadMore: (parentUid: string, offset: number) => void
    onLoadMoreKind: (anchorId: string, kind: EntityKind, offset: number) => void
    onToggleKind: (assetUid: string, kind: EntityKind) => void
}

function AssetNodeComponent({ data, id }: NodeProps) {
    const { t } = useTranslation('graph')
    const d = data as AssetNodeData
    const node = d.graph

    // "+N more" pseudo-node: a single button that loads the next children page.
    if (node.kind === 'more') {
        return (
            <div className="relative">
                <Handle type="target" position={Position.Top} className="!bg-muted-foreground/40" />
                <button
                    type="button"
                    data-testid={`graph-node-more-${node.moreKind ? `${node.moreKind}-` : ''}${node.moreParentUid}`}
                    onClick={(e) => {
                        e.stopPropagation()
                        if (node.moreKind) {
                            d.onLoadMoreKind(node.moreParentUid!, node.moreKind, node.moreNextOffset ?? 0)
                        } else {
                            d.onLoadMore(node.moreParentUid!, node.moreNextOffset ?? 0)
                        }
                    }}
                    disabled={d.loading}
                    className="flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-sm hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
                >
                    <AppIcon path={mdiPlusCircleOutline} size="xs" />
                    {t('node.more', { count: node.moreRemaining ?? 0 })}
                </button>
            </div>
        )
    }

    const accent = assetTypeColorVar(node.assetTypeCode)
    const pictureUrl = node.pictureDocumentUid
        ? `${d.apiBaseUrl}/documents/${node.pictureDocumentUid}/content`
        : undefined

    return (
        <div
            data-testid={`graph-node-${node.assetUid}`}
            data-focal={d.isFocal ? 'true' : 'false'}
            className={`relative w-[180px] rounded-lg border bg-card text-card-foreground shadow-sm ${
                d.isFocal ? 'ring-2 ring-offset-2 ring-offset-background' : ''
            }`}
            style={{
                borderColor: accent,
                borderLeftWidth: 4,
                ...(d.isFocal ? { boxShadow: `0 0 0 2px ${accent}` } : {}),
            }}
        >
            <Handle type="target" position={Position.Top} className="!bg-muted-foreground/40" />
            <Handle type="source" position={Position.Bottom} className="!bg-muted-foreground/40" />

            {/* Parent (upward) expansion — a dedicated top-edge chevron so a body
                click (which expands downward) never explodes the view upward. */}
            {d.parentUid && !d.parentExpanded && (
                <button
                    type="button"
                    data-testid={`graph-expand-parent-${node.assetUid}`}
                    aria-label={t('node.expand_parent')}
                    title={t('node.expand_parent')}
                    onClick={(e) => {
                        e.stopPropagation()
                        d.onExpand(d.parentUid!)
                    }}
                    className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 rounded-full border border-border bg-card p-0.5 text-muted-foreground shadow-sm hover:bg-accent hover:text-accent-foreground"
                >
                    <AppIcon path={mdiChevronUp} size="sm" />
                </button>
            )}

            <div className="flex items-start gap-2 p-2.5">
                {pictureUrl ? (
                    <img
                        src={pictureUrl}
                        alt=""
                        className="h-9 w-9 shrink-0 rounded object-cover"
                        onError={(e) => {
                            ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                        }}
                    />
                ) : node.icon?.emoji ? (
                    // A per-asset emoji the user chose: kept, on a neutral tile.
                    <span
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-lg leading-none"
                        style={{
                            backgroundColor:
                                'color-mix(in oklch, var(--gradient-neutral-from) 10%, transparent)',
                        }}
                        aria-hidden
                    >
                        {node.icon.emoji}
                    </span>
                ) : node.icon ? (
                    <IconTile path={node.icon.path} gradient={node.icon.gradient} size="md" />
                ) : null}
                <div className="min-w-0 flex-1">
                    <div
                        className="truncate text-sm font-medium"
                        title={node.name}
                        data-testid={`graph-node-name-${node.assetUid}`}
                    >
                        {node.name}
                    </div>
                    {node.assetTypeCode && (
                        <div className="truncate text-[11px] text-muted-foreground" style={{ color: accent }}>
                            {node.assetTypeCode}
                        </div>
                    )}
                </div>
            </div>

            {node.properties.length > 0 && (
                <div
                    className="border-t border-border px-2.5 py-1.5"
                    data-testid={`graph-node-properties-${node.assetUid}`}
                >
                    {node.properties.slice(0, 3).map((p) => (
                        <div
                            key={p.code}
                            className="flex justify-between gap-2 text-[11px]"
                            data-testid={`graph-node-property-${node.assetUid}-${p.code}`}
                        >
                            <span className="truncate text-muted-foreground">{p.code}</span>
                            <span className="truncate font-medium">{p.value}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Non-asset kinds, pulled in on demand. Each toggle appends/removes a
                `<kind>@<uid>` token in the URL's expanded state. */}
            {node.assetUid && (
                <div className="flex items-center justify-start gap-0.5 border-t border-border px-1.5 py-1">
                    {KIND_TOGGLES.map(({ kind, icon }) => {
                        const active = d.expandedKinds?.has(kind) ?? false
                        const count = node.linkCounts?.[kind] ?? 0
                        return (
                            <button
                                key={kind}
                                type="button"
                                data-testid={`graph-kind-${kind}-${node.assetUid}`}
                                aria-label={t(`kind.${kind}`)}
                                aria-pressed={active}
                                title={t(`kind.${kind}`)}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    d.onToggleKind(node.assetUid!, kind)
                                }}
                                className={`relative rounded p-1 hover:bg-accent hover:text-accent-foreground ${
                                    active ? 'text-foreground bg-accent' : 'text-muted-foreground'
                                }`}
                            >
                                <AppIcon path={icon} size="xs" />
                                {count > 0 && (
                                    <span
                                        data-testid={`graph-kind-count-${kind}-${node.assetUid}`}
                                        className="absolute -right-1 -top-1 min-w-[14px] rounded-full px-1 text-center text-[9px] font-semibold leading-[14px] text-white"
                                        style={{ backgroundColor: kindColorVar(kind) }}
                                    >
                                        {count > 99 ? '99+' : count}
                                    </span>
                                )}
                            </button>
                        )
                    })}
                    {/* Typed asset relations are not an expandable kind — the
                        hierarchy/relation edges are already drawn — so the
                        count rides along as a plain badge (spec
                        2026-08-16-03 §6). */}
                    <AssetRelationsBadge count={node.relationsCount} className="ml-1" />
                </div>
            )}

            <div className="flex items-center justify-end gap-1 border-t border-border px-1.5 py-1">
                {/* The focal node is always expanded (its neighbors are the graph's
                    seed), so it carries no expand/collapse toggle. */}
                {d.isFocal ? null : d.isExpanded ? (
                    <button
                        type="button"
                        data-testid={`graph-collapse-${node.assetUid}`}
                        aria-label={t('node.collapse')}
                        title={t('node.collapse')}
                        onClick={(e) => {
                            e.stopPropagation()
                            d.onCollapse(id)
                        }}
                        className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    >
                        <AppIcon path={mdiMinusCircleOutline} size="sm" />
                    </button>
                ) : (
                    <button
                        type="button"
                        data-testid={`graph-expand-${node.assetUid}`}
                        aria-label={t('node.expand')}
                        title={t('node.expand')}
                        onClick={(e) => {
                            e.stopPropagation()
                            d.onExpand(id)
                        }}
                        disabled={d.loading}
                        className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                    >
                        <AppIcon path={mdiPlusCircleOutline} size="sm" />
                    </button>
                )}
                <button
                    type="button"
                    data-testid={`graph-open-${node.assetUid}`}
                    aria-label={t('node.open')}
                    title={t('node.open')}
                    onClick={(e) => {
                        e.stopPropagation()
                        if (node.assetUid) d.onOpen(node.assetUid)
                    }}
                    className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                >
                    <AppIcon path={mdiOpenInNew} size="sm" />
                </button>
            </div>
        </div>
    )
}

export const AssetNode = memo(AssetNodeComponent)
